import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import FAQSection from '@/components/base/FAQSection';
import { articles, categories, testimonials } from '@/mocks/data';

const featuredArticles = articles.filter((a) => a.featured);

const categoryMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryMap[c.slug] = c.name;
});

const faqs = [
  {
    question: '我工作很忙，怎么抽出时间陪伴孩子学习？',
    answer: '时间不在于长短而在于质量。建议每天固定15-30分钟的"专属时间"，放下手机和工作，全身心投入到和孩子的互动中。周末可以安排一次较长的亲子学习时光。关键在于坚持和专注，而不是时长。',
  },
  {
    question: '孩子成绩一直上不去，我该怎么帮他？',
    answer: '首先需要找到成绩上不去的根本原因：是基础知识薄弱、学习方法不对、还是心理压力太大？建议从一次考试分析入手，和孩子一起找出薄弱环节，有针对性地制定提升计划。如果自己无法判断，可以寻求老师或专业教育顾问的帮助。',
  },
  {
    question: '孩子不爱和我沟通怎么办？',
    answer: '尝试改变沟通方式：从"审问"变为"分享"。不要一开口就问"作业写完了吗""考试多少分"，可以先分享自己今天遇到的有趣的事情，创造轻松的对话氛围。另外，选择孩子心情好的时机沟通，比如散步时、睡前等放松时刻。',
  },
  {
    question: '如何判断孩子是否需要课外辅导？',
    answer: '课外辅导不是必需品，而是"对症下药"的工具。判断标准：孩子是否在某科目上持续落后于班级平均水平？孩子自己是否有提升的意愿？如果孩子只是偶尔波动、或者内心抗拒补习，强制报班往往适得其反。',
  },
  {
    question: '文章中的方法适合多大的孩子？',
    answer: '我们的内容覆盖6-18岁（小学到高中）的孩子。每篇文章都会明确标注适用的年龄段或学段，妈妈可以根据自己孩子的情况选择阅读。不同年龄段的教育方法有所不同，请注意区分。',
  },
];

export default function Home() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('订阅成功！感谢您的关注。');
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9tm8053pcjqs2fcsgdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('订阅成功！感谢您的关注，我们会定期为您推送优质内容。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="首页"
        description="学生的妈妈是孩子学习路上最重要的支持者。提供学习陪伴、情绪管理、亲子沟通与升学规划的实用方法，帮助每一位学生的妈妈从焦虑走向从容。"
        keywords="学生的妈妈,学生妈妈陪读技巧,如何辅导孩子学习,学生家长情绪管理,亲子沟通方法,孩子成绩提升,升学规划指南"
        canonicalUrl="https://lijinye.cn"
      />
      <SchemaOrg type="WebSite" data={{ name: '学生的妈妈', url: 'https://lijinye.cn', description: '陪伴孩子成长的智慧指南', potentialAction: { '@type': 'SearchAction', target: 'https://lijinye.cn/search?q={search_term_string}', 'query-input': 'required name=search_term_string' } }} />
      <SchemaOrg type="Organization" data={{ name: '学生的妈妈', url: 'https://lijinye.cn', description: '陪伴孩子成长的智慧指南，为学生的妈妈提供学习陪伴、情绪管理、亲子沟通与升学规划的实用方法。', foundingDate: '2024', contactPoint: { '@type': 'ContactPoint', contactType: 'customer service', email: 'hello@lijinye.cn' } }} />
      <SchemaOrg type="FAQPage" data={{ mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20minimalist%20illustration%20of%20a%20mother%20and%20child%20reading%20together%20surrounded%20by%20soft%20floating%20leaves%20and%20books%2C%20cream%20background%20with%20warm%20coral%20and%20amber%20accents%2C%20gentle%20atmospheric%20light%2C%20dreamy%20and%20tender%20mood%2C%20artistic%20style%2C%20no%20text&width=1600&height=900&seq=hero-main-01&orientation=landscape"
                alt="学生的妈妈 - 陪伴孩子成长的智慧指南"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
            <div className="relative z-10 w-full px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight max-w-4xl mx-auto">
                陪伴孩子成长的
                <span className="block text-primary-300">智慧指南</span>
              </h1>
              <p className="mt-5 md:mt-7 text-base md:text-lg text-background-200 max-w-2xl mx-auto leading-relaxed">
                帮助每一位学生的妈妈从焦虑走向从容，用科学的方法和温暖的态度陪伴孩子成长，成为孩子最有力的后盾。
              </p>
              <div className="mt-8 md:mt-10">
                <CategoryTabs variant="hero" />
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  每一位妈妈，都是孩子最好的老师
                </h2>
                <p className="mt-4 text-foreground-600 leading-relaxed">
                  我们深知，在陪伴孩子学习的路上，妈妈们有着太多的困惑与焦虑。从辅导作业的"鸡飞狗跳"到青春期的沟通障碍，从升学规划的迷茫到情绪管理的无力——<strong>学生的妈妈</strong>为你提供科学、温暖、可落地的解决方案。
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={`/category/${cat.slug}`}
                    className="group flex flex-col items-center text-center p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                  >
                    <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${cat.icon} text-2xl`}></i>
                    </span>
                    <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
                      {cat.name}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                      {cat.description}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  精选文章
                </h2>
                <p className="mt-3 text-foreground-600">
                  妈妈们最关心的热门内容，为你精选推荐
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <ArticleCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    category={article.category}
                    excerpt={article.excerpt}
                    imageUrl={article.imageUrl}
                    author={article.author}
                    readTime={article.readTime}
                    date={article.date}
                    tags={article.tags}
                    categoryName={categoryMap[article.category]}
                    categorySlug={article.category}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* All Articles by Category Tabs */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  全部文章
                </h2>
                <p className="mt-3 text-foreground-600">
                  覆盖学习陪伴、情绪管理、亲子沟通、升学规划四大板块
                </p>
              </div>
              <div className="flex justify-center mb-10">
                <CategoryTabs />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.slice(0, 9).map((article) => (
                  <ArticleCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    category={article.category}
                    excerpt={article.excerpt}
                    imageUrl={article.imageUrl}
                    author={article.author}
                    readTime={article.readTime}
                    date={article.date}
                    tags={article.tags}
                    categoryName={categoryMap[article.category]}
                    categorySlug={article.category}
                  />
                ))}
              </div>
              <div className="text-center mt-10">
                <a
                  href="/category/study-companion"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部文章
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  妈妈们怎么说
                </h2>
                <p className="mt-3 text-foreground-600">
                  来自真实用户的反馈与心声
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex flex-col"
                  >
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="mt-4 pt-3 border-t border-background-200/70">
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.child}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <FAQSection faqs={faqs} />
            </div>
          </section>

          {/* Newsletter */}
          <section className="py-14 md:py-20 bg-accent-500">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-50/25 text-background-50">
                <i className="ri-mail-send-line text-2xl"></i>
              </span>
              <h2 className="mt-5 text-2xl md:text-3xl font-heading font-bold text-background-50">
                订阅新闻简报
              </h2>
              <p className="mt-3 text-background-100">
                每周精选家庭教育干货，直送您的邮箱。与50,000+妈妈一起成长。
              </p>
              <form
                onSubmit={handleSubscribe}
                data-readdy-form
                className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入您的邮箱地址"
                  required
                  className="flex-1 px-4 py-3 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 text-sm outline-none focus:ring-2 focus:ring-background-50/50"
                />
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="website-alt-field"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-6 py-3 rounded-lg bg-foreground-950 text-background-50 font-medium text-sm hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '提交中...' : '立即订阅'}
                </button>
              </form>
              {formStatus === 'success' && (
                <p className="mt-3 text-sm text-background-50 font-medium">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="mt-3 text-sm text-background-50 font-medium">{formMessage}</p>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}